import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  DatePicker,
  Select,
  InputNumber,
  Space,
} from "antd";
import {
  PlusOutlined,
  MinusCircleOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";

import { getAPI, postAPI, displayMessage } from "../../utils/common";
import { SUCCESS_MESSAGE, ERROR_MSG_TYPE } from "../../constants/messages";
import { KITCHEN_ITEM_LIST, KITCHEN_ISSUE_CREATE } from "../../constants/api";
import LS_SERVICE from "../../utils/localStorage";
import { ISSUE_DEPARTMENT } from "../../constants/constent";

function KitchenIssueAdd() {
  const { Content } = Layout;
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    fetchItems();
    form.setFieldsValue({
      issue_by: LS_SERVICE.get("name"),
    });
  }, []);

  const fetchItems = () => {
    getAPI(
      KITCHEN_ITEM_LIST,
      (res) => {
        if (res.status === true) {
          setItems(res.data);
        }
      },
      () => {},
    );
  };

  const createData = (values) => {
    setLoading(true);

    const handOverToValue = Array.isArray(values.hand_over_to)
      ? values.hand_over_to[0]
      : values.hand_over_to;

    const payload = {
      issue_date: dayjs(values.issue_date).format("YYYY-MM-DD"),
      remark: values.remark,
      hand_over_to: handOverToValue,
      issue_by: values.issue_by,
      items: values.items.map((row) => ({
        item_id: row.item_id,
        qty: row.qty,
        purpose: row.purpose,
      })),
    };

    postAPI(
      KITCHEN_ISSUE_CREATE,
      payload,
      (res) => {
        if (res.status === true) {
          displayMessage(SUCCESS_MESSAGE, res.message);
          navigate("/kitchen/issue");
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
        setLoading(false);
      },
      () => {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to create kitchen issue");
      },
    );
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Add Item Issue"
          bordered={false}
          extra={
            <Button type="primary" ghost onClick={() => navigate(-1)}>
              <RollbackOutlined />
            </Button>
          }
        >
          <Spin spinning={loading}>
            <Form
              form={form}
              layout="vertical"
              onFinish={createData}
              initialValues={{ items: [{}] }}
            >
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Form.Item
                    name="issue_date"
                    label="Issue Date & Time"
                    rules={[{ required: true }]}
                    initialValue={dayjs()}
                  >
                    <DatePicker
                      showTime
                      format="YYYY-MM-DD HH:mm:ss"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item name="issue_by" label="Issue By">
                    <Input placeholder="Issue By" />
                  </Form.Item>
                </Col>

                <Col span={4}>
                  <Form.Item
                    name="hand_over_to"
                    label="Hand Over To (Department)"
                    rules={[{ required: true }]}
                  >
                    <Select
                      // mode="tags"
                      maxCount={1}
                      placeholder="Select one department or enter custom text"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option?.children
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {ISSUE_DEPARTMENT.map((item) => (
                        <Select.Option key={item.value} value={item.value}>
                          {item.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={10}>
                  <Form.Item name="remark" label="Remark">
                    <Input placeholder="Optional remark" />
                  </Form.Item>
                </Col>
              </Row>

              <Card title="Issue Items" size="small">
                <Form.List name="items">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Row gutter={[16, 16]} key={key}>
                          <Col span={6}>
                            <Form.Item
                              {...restField}
                              name={[name, "item_id"]}
                              label="Item"
                              rules={[{ required: true }]}
                            >
                              <Select
                                placeholder="Select item"
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  option?.children
                                    ?.toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                              >
                                {items.map((item) => (
                                  <Select.Option key={item.id} value={item.id}>
                                    {item.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>

                          <Col span={4}>
                            <Form.Item shouldUpdate noStyle>
                              {({ getFieldValue }) => {
                                const itemId = getFieldValue([
                                  "items",
                                  name,
                                  "item_id",
                                ]);

                                const selectedItem = items.find(
                                  (item) => item.id === itemId,
                                );

                                return (
                                  <Form.Item
                                    {...restField}
                                    name={[name, "qty"]}
                                    label="Qty"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Qty is required",
                                      },
                                      {
                                        validator: (_, value) => {
                                          if (
                                            value === undefined ||
                                            value === null
                                          ) {
                                            return Promise.resolve();
                                          }

                                          // allow positive numbers including decimals like 0.5
                                          return value > 0
                                            ? Promise.resolve()
                                            : Promise.reject(
                                                new Error(
                                                  "Only positive numbers allowed",
                                                ),
                                              );
                                        },
                                      },
                                    ]}
                                  >
                                    <InputNumber
                                      min={0.001}
                                      step={0.001}
                                      precision={3}
                                      suffix={`${selectedItem?.unit ? `${selectedItem.unit} - (${selectedItem.current_stock}${selectedItem.unit})` : ""}`}
                                      style={{ width: "100%" }}
                                    />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </Col>

                          <Col span={10}>
                            <Form.Item
                              {...restField}
                              name={[name, "purpose"]}
                              label="Purpose"
                            >
                              <Input placeholder="Ex: Lunch preparation" />
                            </Form.Item>
                          </Col>

                          <Col
                            span={4}
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Button
                              danger
                              onClick={() => remove(name)}
                              icon={<MinusCircleOutlined />}
                            />
                          </Col>
                        </Row>
                      ))}

                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                        >
                          Add Item
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
              </Card>

              <Row>
                <Col span={24}>
                  <Form.Item style={{ float: "right", marginTop: 16 }}>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Submit Issue
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Card>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default KitchenIssueAdd;
